import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus =
  | 'idle'      // noch nichts editiert
  | 'unsaved'   // Aenderung erfasst, Debounce laeuft
  | 'saving'    // Speichern laeuft
  | 'saved'     // erfolgreich gespeichert
  | 'error'     // Speichern fehlgeschlagen
  | 'blocked';  // canSave === false (z.B. Pflichtfeld leer)

interface UseAutosaveOptions<T> {
  /** Debounce in ms nach der letzten Aenderung, bevor gespeichert wird. */
  delay?: number;
  /** Fuehrt den eigentlichen Save aus. Wirft bei Fehler. */
  onSave: (values: T) => Promise<void>;
  /** Optionales Gate: nur speichern, wenn true (z.B. Pflichtfelder gefuellt). */
  canSave?: (values: T) => boolean;
}

export interface UseAutosaveResult<T> {
  status: AutosaveStatus;
  /** True, solange seit dem letzten Server-Sync editiert wurde. Fuer den
   *  Re-Sync-Guard: das Formular NICHT aus Server-Daten ueberschreiben, solange
   *  dirty (verhindert Cursor-Springen waehrend des Tippens). */
  isDirty: boolean;
  /** Bei jeder Aenderung mit dem KOMPLETTEN aktuellen Formularstand aufrufen. */
  schedule: (values: T) => void;
  /** Sofort speichern (z.B. on blur / vor Navigation / Retry). */
  flush: () => void;
}

/**
 * Generischer Autosave-Hook: debounced Speichern mit Status, Dirty-Tracking und
 * optionalem canSave-Gate. Saves werden serialisiert — kommt waehrend eines
 * laufenden Saves eine neue Aenderung, wird danach erneut gespeichert.
 */
export function useAutosave<T>({ delay = 600, onSave, canSave }: UseAutosaveOptions<T>): UseAutosaveResult<T> {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [isDirty, setIsDirty] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<T | null>(null);
  const inFlight = useRef(false);
  const pending = useRef(false);
  const mounted = useRef(true);

  // onSave/canSave per Ref frisch halten, ohne die Callbacks neu zu erzeugen.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;

  const setSafe = useCallback((s: AutosaveStatus) => {
    if (mounted.current) setStatus(s);
  }, []);

  const run = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const values = latest.current;
    if (values === null) return;
    if (canSaveRef.current && !canSaveRef.current(values)) { setSafe('blocked'); return; }
    if (inFlight.current) { pending.current = true; return; }

    inFlight.current = true;
    setSafe('saving');
    try {
      await onSaveRef.current(values);
      if (mounted.current) setIsDirty(false);
      setSafe('saved');
    } catch {
      setSafe('error');
    } finally {
      inFlight.current = false;
      if (pending.current) {
        pending.current = false;
        void run(); // waehrend des Saves kam Neues -> erneut
      }
    }
  }, [setSafe]);

  const schedule = useCallback((values: T) => {
    latest.current = values;
    if (mounted.current) setIsDirty(true);
    setSafe(inFlight.current ? 'saving' : 'unsaved');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void run(); }, delay);
  }, [delay, run, setSafe]);

  const flush = useCallback(() => { void run(); }, [run]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      // Letzte Aenderung best-effort rausschreiben (kein setState mehr nach Unmount).
      const values = latest.current;
      if (values !== null && !inFlight.current && (!canSaveRef.current || canSaveRef.current(values))) {
        void onSaveRef.current(values).catch(() => undefined);
      }
    };
  }, []);

  return { status, isDirty, schedule, flush };
}
